import { supabase } from '@/lib/supabase';

const SUPABASE_URL = import.meta.env.PUBLIC_SUPABASE_URL;
const SUPABASE_ANON_KEY = import.meta.env.PUBLIC_SUPABASE_ANON_KEY;

async function invokeFunction<T>(
  name: string,
  body: Record<string, unknown>,
  token: string,
): Promise<T> {
  const res = await fetch(`${SUPABASE_URL}/functions/v1/${name}`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(body),
  });
  const data = (await res.json().catch(() => ({}))) as {
    url?: string;
    error?: string;
    message?: string;
  };
  if (!res.ok) {
    throw new StripeFunctionError(res.status, data.error ?? 'unknown_error', data.message);
  }
  return data as T;
}

export class StripeFunctionError extends Error {
  constructor(
    public readonly status: number,
    public readonly code: string,
    message?: string,
  ) {
    super(message ?? code);
    this.name = 'StripeFunctionError';
  }
}

export async function startCheckout(args: {
  tier: 'monthly' | 'annual';
  claimFounder: boolean;
  returnUrl: string;
  cancelUrl: string;
}): Promise<string> {
  const { data: sessionData } = await supabase.auth.getSession();
  const token = sessionData.session?.access_token;
  if (!token) throw new Error('Not signed in');
  const result = await invokeFunction<{ url: string }>(
    'stripe-create-checkout',
    {
      tier: args.tier,
      claim_founder: args.claimFounder,
      return_url: args.returnUrl,
      cancel_url: args.cancelUrl,
    },
    token,
  );
  return result.url;
}

/**
 * One-off donation checkout. Anonymous — no session required (the function runs
 * verify_jwt=false); we pass the public anon key so the gateway accepts the call.
 * Distinct from startCheckout, which is the subscription flow.
 */
export async function startDonateCheckout(amountCents: number): Promise<string> {
  const res = await fetch(`${SUPABASE_URL}/functions/v1/donate-checkout`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      apikey: SUPABASE_ANON_KEY,
      Authorization: `Bearer ${SUPABASE_ANON_KEY}`,
    },
    body: JSON.stringify({ amount_cents: amountCents }),
  });
  const data = (await res.json().catch(() => ({}))) as {
    url?: string;
    error?: string;
    message?: string;
  };
  if (!res.ok || !data.url) {
    throw new StripeFunctionError(res.status, data.error ?? 'unknown_error', data.message);
  }
  return data.url;
}

export async function startBillingPortal(): Promise<string> {
  const { data: sessionData } = await supabase.auth.getSession();
  const token = sessionData.session?.access_token;
  if (!token) throw new Error('Not signed in');
  const result = await invokeFunction<{ url: string }>('stripe-billing-portal', {}, token);
  return result.url;
}
